import { SelectMenu, SelectMenuItem } from '../../components/ui';
import { useT } from '../../i18n';
import { useEnvironments } from './api';

/** Sentinel for "no environment": a select value is always a string. */
const NONE = 'none';

export interface EnvironmentSelectProps {
  projectCode: string;
  value: string | null;
  onChange: (value: string | null) => void;
  id?: string;
  invalid?: boolean;
  describedBy?: string;
  /** Lets a closed dialog mount the field without fetching the list. */
  enabled?: boolean;
}

/**
 * Picks one of the project's environments, or none. The list is the project's
 * own: an environment is added in the project settings, never typed here, so
 * the same stand is not written three ways.
 */
export const EnvironmentSelect = ({
  projectCode,
  value,
  onChange,
  id,
  invalid,
  describedBy,
  enabled = true,
}: EnvironmentSelectProps) => {
  const { t } = useT();
  const { data: environments } = useEnvironments(projectCode, { enabled });

  return (
    <SelectMenu
      // Radix reads the name of the selected option from the dropdown, which
      // is mounted only while it is open. A value that arrives after mount —
      // the environment of the case being edited — would therefore leave the
      // field looking empty, so the select is remounted with it.
      key={value ?? NONE}
      id={id}
      invalid={invalid}
      aria-describedby={describedBy}
      value={value ?? NONE}
      onValueChange={(next) => onChange(next === NONE ? null : next)}
    >
      <SelectMenuItem value={NONE}>{t('environments.none')}</SelectMenuItem>
      {(environments ?? []).map((environment) => (
        <SelectMenuItem key={environment.id} value={environment.id}>
          {environment.name}
        </SelectMenuItem>
      ))}
    </SelectMenu>
  );
};
